import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "src/models/api/load";
import { PropsFilter } from "src/models/api/props-filter";
import { ClassifyMailFilter } from "src/models/classify-mail/classify-mail-filter";
import { DocumentStatusRef } from "src/models/documents/document-statuses";
import { IncomingMailDTO } from "src/models/incoming-mail";
import { MailClassificationStatusRef } from "src/models/mail-classification-status/mail-classification-status-ref";
import { OutgoingMailDTO } from "src/models/outgoing-mail";
import { ClassifyIncomingMailApiService } from "src/services/classify-incoming-mail/classify-incoming-mail-api.service";
import { ClassifyOutgoingMailApiService } from "src/services/classify-outgoing-mail/classify-outgoing-mail-api.service";
import { saveDocument } from "src/services/documents";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import { DOCUMENTS_STATUS_TYPES, MAIL_CLASSIFICATION_STATUS } from "src/utils/constants/constants";
import { Props, State } from "./interfaces";
import { DocumentDTO } from "../../models/documents";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import DocumentFormfields from "../Document/DocumentFormfields/DocumentFormfields";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const ClassifyMail = ({ outgoingMail, incomingMail, handleNotifyParent }: Props) => {
  const tRP = "components.classify_mail";
  const { t } = useTranslation();
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      error: "",
      document: (outgoingMail?.processedDocument ?? incomingMail?.document) as DocumentDTO,
      willNotBeClassified: false,
      CCDOpen: false,
    }),
    [outgoingMail, incomingMail]
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region handle change
  const handleChange = useCallback((field: keyof DocumentDTO, value: DocumentDTO[keyof DocumentDTO]) => {
    setState((state) => ({
      ...state,
      document: {
        ...state.document,
        [field]: value,
      },
    }));
  }, []);
  //#endregion

  //#region save classfification status
  const handleSaveClassificationStatus = useCallback(
    async (status: MailClassificationStatusRef) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        let response: LoadResponse<OutgoingMailDTO | IncomingMailDTO> | null = null;

        if (incomingMail) {
          response = await new ClassifyIncomingMailApiService().loadItems({
            ids: [incomingMail.id],
            classificationStatusId: status.id,
          } as ClassifyMailFilter);
        } else if (outgoingMail) {
          response = await new ClassifyOutgoingMailApiService().loadItems({
            ids: [outgoingMail.id],
            classificationStatusId: status.id,
          } as ClassifyMailFilter);
        }
        if (response?.error) {
          setState((state) => ({
            ...state,
            error: response?.error ?? "",
          }));
        } else {
          setState({
            ...INITIAL_STATE,
          });
          handleNotifyParent();
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [INITIAL_STATE, outgoingMail, incomingMail]
  );
  //#endregion

  //#region save classified document
  const handleSaveClassifiedDocument = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await saveDocument(
        {
          ...state.document,
          companyAccount: state.document.legalEntity?.companyAccount ?? null,
          status: DOCUMENTS_STATUS_TYPES.VERIFIED as DocumentStatusRef,
        },
        null,
        true,
        true,
        true
      );
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        handleSaveClassificationStatus(MAIL_CLASSIFICATION_STATUS.Classified as MailClassificationStatusRef);
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.document, handleSaveClassificationStatus]);
  //#endregion

  //#region confirm will not be classified
  const handleConfirmWillNotBeClassified = useCallback(() => {
    handleSaveClassificationStatus(MAIL_CLASSIFICATION_STATUS.WillNotBeClassified as MailClassificationStatusRef);
  }, [handleSaveClassificationStatus]);
  //#endregion

  //#region handle save
  const handleSave = useCallback(() => {
    if (state.willNotBeClassified) {
      handleConfirmWillNotBeClassified();
    } else {
      handleSaveClassifiedDocument();
    }
  }, [state.willNotBeClassified, handleSaveClassifiedDocument, handleConfirmWillNotBeClassified]);
  //#endregion

  return (
    <>
      {outgoingMail?.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.WillNotBeClassified.id ||
      incomingMail?.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.WillNotBeClassified.id ? (
        <Typography
          color={theme.palette.secondary.light}
          title={`${t(`${tRP}.will_not_be_classified_button_title`)}`}
        >
          {`${t(`${tRP}.button_title`)}`} &nbsp;
        </Typography>
      ) : !!outgoingMail && !outgoingMail.processedDocument ? (
        <Typography
          color={theme.palette.secondary.light}
          title={`${t(`${tRP}.missing_processed_document`)}`}
        >
          {`${t(`${tRP}.button_title`)}`} &nbsp;
          <FontAwesomeIcon icon={faCircleExclamation} />
        </Typography>
      ) : (
        <Button
          onClick={() => setState((state) => ({ ...state, open: true }))}
          style={{ color: theme.palette.text.primary }}
        >
          {`${t(`${tRP}.button_title`)}`}
        </Button>
      )}

      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            error={state.error}
            loading={state.loading}
          >
            <Grid>
              <DocumentFormfields
                document={state.document}
                handleChangeDocumentField={handleChange}
                propsFilterForLegalentity={
                  {
                    contextAuthorizations: [AUTHORIZATIONS.LegalEntityAccess.ProcessIncomingDocuments],
                  } as PropsFilter
                }
                readOnlyFields={{ isStatusRO: true }}
                hideReadOnlyFields
                customURLForLegalEntity={"/GetCompanyLegalEntitiesAcrossCompanyAccount"}
                allowModifyEmployee
              />

              <Divider
                orientation="horizontal"
                color={theme.palette.secondary.light}
                style={{ marginTop: "20px" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.willNotBeClassified}
                    onChange={(_, checked) => setState((state) => ({ ...state, willNotBeClassified: checked }))}
                  />
                }
                label={`${t(`${tRP}.will_not_be_classified`)}`}
              />
            </Grid>
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState(() => ({ ...state, isCCDOpen: true }))}
          handleSave={handleSave}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState(() => ({ ...state, isCCDOpen: false }))}
        handleDiscard={() => setState({ ...INITIAL_STATE })}
      />
    </>
  );
};

export default ClassifyMail;
